/**
 * GuideTemplate — presentational body for a CIH V3 Guide detail page
 * (/coaching-business/[slug]).
 *
 * Pure render component: takes a published guide row plus its annotated Tiptap
 * body + heading anchors and renders the hero, article body, optional pull
 * quote, reviewer byline, FAQ section, and the stage-matched Coach Business
 * Builder CTA. Kept free of loaders/router data so it is render-testable in
 * isolation — the route component calls it with `Route.useLoaderData()`.
 *
 * Aliases: guide template, guide detail body, cbb guide page
 */
import { ArticleBody } from "~/components/cih/article-body";
import { CbbCta } from "~/components/cih/cbb-cta";
import { CihFaqSection } from "~/components/cih/faq-section";
import { CihHero } from "~/components/cih/hero";
import { CihPullQuoteBand } from "~/components/cih/pull-quote-band";
import { CihReviewerByline } from "~/components/cih/reviewer-byline";
import { CihSection } from "~/components/cih/section";
import { CihSectionHeader } from "~/components/cih/section-header";
import { SeriesNav } from "~/components/cih/series-nav";
import type { GuideRow } from "~/lib/server/cih/guides";
import type { PlaybookRow } from "~/lib/server/cih/playbooks";
import type { BodyHeading } from "~/lib/shared/cih/body-toc";
import type { SeriesNavResult } from "~/lib/shared/cih/library-series";
import { GUIDE_TYPE_LABELS, type GuideType } from "~/lib/shared/content-status";
import type { JsonValue } from "~/lib/shared/json";

/** A series can mix guides and playbooks — see `library-series.ts`. */
type GuideSeriesNav = SeriesNavResult<
  GuideRow & { slug: string },
  PlaybookRow & { slug: string }
>;

type Props = {
  readonly guide: GuideRow;
  /** Tiptap doc with stable heading `id` attrs (from annotateBodyHeadings). */
  readonly bodyDoc: JsonValue | null;
  /** Ordered H2 anchors (unused for nav today; kept for parity + future use). */
  readonly headings: BodyHeading[];
  /** Previous/next series nav, or null when the guide belongs to no series. */
  readonly seriesNav?: GuideSeriesNav | null;
};

export function GuideTemplate({
  guide,
  bodyDoc,
  headings: _headings,
  seriesNav,
}: Props) {
  const faqs = guide.faqs ?? [];
  const typeLabel =
    GUIDE_TYPE_LABELS[guide.guideType as GuideType] ??
    GUIDE_TYPE_LABELS.stage_guide;

  return (
    <>
      <CihHero
        breadcrumb={[
          { label: "Home", href: "/" },
          { label: "Coaching Business", href: "/coaching-business" },
          {
            label: guide.title,
            href: guide.slug
              ? `/coaching-business/${guide.slug}`
              : "/coaching-business",
          },
        ]}
        kicker={typeLabel}
        kickerColorClass="text-green-700"
        title={guide.title}
        lede={guide.description}
      />

      <CihReviewerByline
        reviewerName={guide.reviewerName}
        reviewerCredentials={guide.reviewerCredentials}
        reviewedDate={guide.reviewedDate}
      />

      {bodyDoc ? (
        <CihSection id="overview" divider={false}>
          <CihSectionHeader label="Guide" title="Overview" />
          <div className="mx-auto max-w-7xl px-4 xl:px-0">
            <ArticleBody
              body={bodyDoc}
              className="prose prose-lg prose-a:text-green-700"
            />
          </div>
        </CihSection>
      ) : null}

      {guide.pullQuote ? (
        <CihPullQuoteBand
          quote={guide.pullQuote}
          attribution={guide.pullQuoteAttribution}
        />
      ) : null}

      {faqs.length > 0 ? (
        <CihSection id="faq">
          <CihFaqSection
            faqs={faqs}
            heading={`Questions about ${guide.title}`}
          />
        </CihSection>
      ) : null}

      {seriesNav ? <SeriesNav nav={seriesNav} /> : null}

      <CbbCta band={guide.ctaStageBand} kicker="Coach Business Builder" />
    </>
  );
}
